/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState, useCallback, SetStateAction, Dispatch } from "react";
import PropTypes from "prop-types";
import { Card, Accordion } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { FormStepTypes } from "constants/";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Quotes from "pages/shipper/ShipmentQuotes/components/quotes/Quotes";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { getProviderOptions, getQuoteRequestDetails, startEditQuoteRequest } from "api";
import { IQuoteRequestData } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";

interface IQuotationGenerationProps {
  onSubmitClick: () => void;
  shipmentId: string;
  quoteRequestGenerated: boolean;
  isInitialize: boolean;
  setIsInitialize: SetStateAction<Dispatch<boolean>>;
}

function QuotationGenerationForm(props: IQuotationGenerationProps) {
  const { onSubmitClick, shipmentId, quoteRequestGenerated, isInitialize, setIsInitialize } = props;
  const [isQuotation, setIsQuotation] = useState<boolean>(true);
  const [data, setData] = useState<IQuoteRequestData>({});
  const [providers, setProviders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const currentActiveStep = searchParams.get("active_step");
  const navigate = useNavigate();
  const intl = useIntl();
  const draftId = searchParams.get("draft_id");
  const urlOrigin = window.location.origin;

  useEffect(() => {
    if (shipmentId) {
      fetchData();
      getProviders();
      startEditQuoteRequest(shipmentId).then((res) => {
        searchParams.set("draft_id", res.data.draft_reference);
        setSearchParams(searchParams);
      });
    }
  }, [shipmentId]);

  const handleSubmitClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmitClick();
  };

  const handleRouteShipment = () => {
    navigate(`/shipment/${shipmentId}`);
  };

  const handleAccordionToggle = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!quoteRequestGenerated && Object.keys(data)?.length === 0) {
      e.stopPropagation();
    }
  };

  const fetchData = () => {
    getQuoteRequestDetails(shipmentId)
      .then((res) => {
        if (res.data) {
          setData(res.data);
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "quote-request-detail.couldNotRetrieveQuoteRequest",
            defaultMessage: "Could not retrieve quote request. Please try again later.",
          }),
        ),
      );
  };

  const getProviders = () => {
    getProviderOptions(shipmentId)
      .then((res) => {
        setProviders(res.data);
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveProvider",
            defaultMessage: "Could not retrieve providers. Please try again later.",
          }),
        );
      });
  };

  function handleFallbackMessage() {
    return (
      <div>
        <span>
          <FormattedMessage
            id="quote-request.quotationNotFoundMessage"
            defaultMessage="We were unable to generate quotations, please try to create a quote request, click here"
          />{" "}
        </span>
        <Link to={`/quote-request?active_step=provider&draft_id=${draftId}`}>
          {urlOrigin}/quote-request?active_step=provider&draft_id={draftId}
        </Link>
      </div>
    );
  }

  const renderSubmitButton = useCallback(() => {
    if (isQuotation) {
      return (
        <Button
          className="rounded-pill mr-2"
          onClick={handleSubmitClick}
          label={intl.formatMessage({
            id: "new-shipment.generateQuotations",
            defaultMessage: "Generate quotations",
          })}
          isLoading={isInitialize}
        />
      );
    }
    return (
      <Button
        className="rounded-pill mr-2"
        onClick={handleRouteShipment}
        label={intl.formatMessage({
          id: "new-shipment.goToShipment",
          defaultMessage: "Go to shipment",
        })}
      />
    );
  }, [isQuotation, isInitialize]);

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header p-0" eventKey={FormStepTypes.CONFIRMATION}>
        <div className="d-flex justify-content-between align-items-center w-100 p-16" onClick={handleAccordionToggle}>
          <h5>
            <FormattedMessage id="new-shipment.reviewQuotationBook" defaultMessage="Review quotations & book" />
          </h5>
          <div className="d-flex align-items-center">
            {currentActiveStep !== FormStepTypes.CONFIRMATION && renderSubmitButton()}
            <SortIcon
              fill="#000"
              width={8}
              height={7}
              transform={`rotate(${currentActiveStep === FormStepTypes.CONFIRMATION ? 180 : 0})`}
            />
          </div>
        </div>
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.CONFIRMATION}>
        <Card.Body>
          <div className="new-shipment-table">
            <Quotes
              id={shipmentId}
              requestStatus={data?.organization?.request_status || ""}
              fetchData={fetchData}
              isQuotation={isQuotation}
              setIsQuotation={setIsQuotation}
              setIsInitialize={setIsInitialize}
              providers={providers}
              fallback={handleFallbackMessage()}
            />
          </div>
          <div className="d-flex align-items-center justify-content-end w-100 px-4">
            <div>{renderSubmitButton()}</div>
          </div>
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

QuotationGenerationForm.propTypes = {
  shipmentId: PropTypes.string.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  quoteRequestGenerated: PropTypes.bool.isRequired,
};

export default QuotationGenerationForm;
